/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CommitStrategy, RealtimeConnection, RealtimeEvents, Scribe } from '@elevenlabs/client';

import type { TranscriptionErrorCode, TranscriptionState } from '../types';
import { resolveAudioInputDeviceConstraint } from '../audio/audioInputDevices';
import { useAiAssistantMicrophoneGlobalState } from '../shared/useAiAssistantMicrophoneGlobalState';

import { ElevenLabsTranscriptionConfig } from './ElevenLabsTranscriptionConfig';
import { useElevenLabsCredentials } from './useElevenLabsCredentials';
import { useTranscriptionSessionEnded } from './useTranscriptionSessionEnded';
import { TranscriptionSilenceTimerOptions, useTranscriptionSilenceTimer } from './useTranscriptionSilenceTimer';
import { UseTranscriptionOptions } from './useTranscription';

export type UseElevenLabsTranscriptionResult = {
   state: TranscriptionState;
   mediaStream: MediaStream | null;
   /** @param showSpeakerLabel - enable diarization (continuous mode) */
   start: (showSpeakerLabel: boolean, config: ElevenLabsTranscriptionConfig) => Promise<void>;
   stop: VoidFunction;
   errorCode: TranscriptionErrorCode | null;
};

/**
 * Scribe v2 Realtime starts processing only after receiving the first 2 seconds
 * of audio. Sending silence on OPEN primes the pipeline so the model is ready
 * to return results as soon as the user actually starts speaking.
 *
 * 2s of PCM 16kHz silence = 64 000 bytes of zeros → ~85 334 chars base64.
 * We split into 5 × 0.5s chunks (16 000 bytes each) to stay well within
 * any single-frame limits.
 */
const WARMUP_CHUNK_SIZE = 16_000; // 0.5s of PCM 16kHz silence (bytes)
const WARMUP_CHUNKS = 5; // 5 × 0.5s = 2.5s total

function buildSilenceBase64(byteLength: number): string {
   const buffer = new Uint8Array(byteLength); // zeros = silence
   let binary = '';
   // eslint-disable-next-line @typescript-eslint/prefer-for-of
   for (let i = 0; i < buffer.length; i++) {
      binary += String.fromCharCode(buffer[i]);
   }
   return btoa(binary);
}

function sendWarmupSilence(connection: RealtimeConnection): void {
   const chunk = buildSilenceBase64(WARMUP_CHUNK_SIZE);
   for (let i = 0; i < WARMUP_CHUNKS; i++) {
      try {
         connection.send({ audioBase64: chunk });
      } catch {
         // Connection may have closed between chunks — ignore
         break;
      }
   }
}

// eslint-disable-next-line max-lines-per-function,max-statements
export const useElevenLabsTranscription = (options: UseTranscriptionOptions): UseElevenLabsTranscriptionResult => {
   const [state, setState] = useState<TranscriptionState>('idle');
   const [errorCode, setErrorCode] = useState<TranscriptionErrorCode | null>(null);
   const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
   const { device } = useAiAssistantMicrophoneGlobalState();
   const deviceRef = useRef(device);
   deviceRef.current = device;

   /*
    * Mirrors state so callbacks and start() can read the latest value without
    * closing over a stale render's copy.
    */
   const stateRef = useRef<TranscriptionState>('idle');

   const setTranscriptionState = useCallback((next: TranscriptionState) => {
      stateRef.current = next;
      setState(next);
   }, []);

   // Stable refs for callbacks — updated on every render, closed over by async handlers
   const onPartialRef = useRef(options.onPartial);
   onPartialRef.current = options.onPartial;
   const onFinalRef = useRef(options.onFinal);
   onFinalRef.current = options.onFinal;
   const onSpeakerTranscriptRef = useRef(options.onSpeakerTranscript);
   onSpeakerTranscriptRef.current = options.onSpeakerTranscript;
   const onErrorRef = useRef(options.onError);
   onErrorRef.current = options.onError;
   const onStopRef = useRef(options.onStop);
   onStopRef.current = options.onStop;
   const onSilenceSecondsRef = useRef(options.onSilenceSeconds);
   onSilenceSecondsRef.current = options.onSilenceSeconds;
   const onSpeakingRef = useRef(options.onSpeaking);
   onSpeakingRef.current = options.onSpeaking;
   const onSilenceDisconnectRef = useRef(options.onSilenceDisconnect);
   onSilenceDisconnectRef.current = options.onSilenceDisconnect;

   /*
    * Session identity. Incremented on every start(). Every async callback
    * captures its own sessionId at creation time and checks it before
    * mutating state — a stale session can never affect a live one.
    * Set to -1 on error/unmount so trailing callbacks become no-ops.
    */
   const sessionIdRef = useRef(0);
   const connectionRef = useRef<RealtimeConnection | null>(null);
   const startTimeRef = useRef<number>(0);

   const { executeAsync: fetchToken } = useElevenLabsCredentials();
   const { executeAsync: reportSessionEnded } = useTranscriptionSessionEnded();

   /*
    * Silence timer options live in a ref so start() can update them for each
    * new session (different config, fresh session-guarded callbacks) without
    * the timer needing to be torn down and recreated.
    */
   const silenceTimerOptionsRef = useRef<TranscriptionSilenceTimerOptions>({
      silenceTimeoutMs: 30_000,
      silenceWarningMs: 10_000,
      onSilenceSeconds: () => undefined,
      onSpeaking: () => undefined,
      onTimeout: () => undefined,
   });
   const silenceTimer = useTranscriptionSilenceTimer(silenceTimerOptionsRef);

   const [currentToken, setCurrentToken] = useState<string | null>(null);

   const reportDuration = useCallback(async () => {
      if (startTimeRef.current > 0) {
         const durationSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
         startTimeRef.current = 0;
         if (durationSeconds > 0 && currentToken) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            await reportSessionEnded({ durationSeconds: durationSeconds, token: currentToken }).finally(() =>
               setCurrentToken(null)
            );
         }
      }
   }, [reportSessionEnded, currentToken]);

   // Cleanup on unmount — close any live session so mic and WS are never leaked.
   useEffect(() => {
      return () => {
         // Invalidate the session so in-flight callbacks are ignored
         sessionIdRef.current = -1;
         if (stateRef.current === 'recording' || stateRef.current === 'connecting') {
            silenceTimer.stop();
            connectionRef.current?.close();
            connectionRef.current = null;
            // Fire and forget — component is unmounting
            // eslint-disable-next-line no-void
            void reportDuration();
         }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // eslint-disable-next-line max-lines-per-function,max-statements
   const start = useCallback(
      // eslint-disable-next-line max-lines-per-function,max-statements
      async (showSpeakerLabel: boolean, config: ElevenLabsTranscriptionConfig): Promise<void> => {
         const { current } = stateRef;
         if (current === 'recording' || current === 'connecting' || current === 'requesting-credentials') {
            return;
         }

         const sessionId = sessionIdRef.current + 1;
         sessionIdRef.current = sessionId;

         setErrorCode(null);
         setMediaStream(null);
         setTranscriptionState('requesting-credentials');

         let token = '';
         try {
            const result = await fetchToken({ showSpeakerLabel: showSpeakerLabel });
            ({ token } = result);
            setCurrentToken(result.token);
         } catch {
            if (sessionIdRef.current === sessionId) {
               setErrorCode('TRANSCRIPTION_CREDENTIALS_UNAVAILABLE');
               setTranscriptionState('error');
               onErrorRef.current?.('TRANSCRIPTION_CREDENTIALS_UNAVAILABLE');
            }
            return;
         }

         if (sessionIdRef.current !== sessionId) {
            return;
         }

         setTranscriptionState('connecting');

         /*
          * Tracks whether a silence countdown was active when the session ended.
          * If true, onSilenceDisconnect fires instead of a plain stop.
          */
         const silenceDisconnectedRef = { current: false };

         /*
          * Configure the silence timer for this session before opening the connection.
          * The timer reads these values on every tick so they're always current.
          * All callbacks are guarded by sessionId to prevent stale sessions from
          * affecting the UI.
          */
         silenceTimerOptionsRef.current = {
            silenceTimeoutMs: config.silenceTimeoutMs,
            silenceWarningMs: config.silenceWarningMs,
            onSilenceSeconds: (secondsRemaining) => {
               if (sessionIdRef.current === sessionId) {
                  silenceDisconnectedRef.current = true;
                  onSilenceSecondsRef.current?.(secondsRemaining);
               }
            },
            onSpeaking: () => {
               if (sessionIdRef.current === sessionId) {
                  silenceDisconnectedRef.current = false;
                  onSpeakingRef.current?.();
               }
            },
            onTimeout: () => {
               if (sessionIdRef.current === sessionId) {
                  // Close the connection — the CLOSE handler takes it from here
                  connectionRef.current?.close();
               }
            },
         };

         /*
          * Helper: handle a graceful stop (session_time_limit, insufficient_audio).
          * These are NOT errors — the session completed normally.
          */
         const handleGracefulStop = (): void => {
            if (sessionIdRef.current !== sessionId) {
               return;
            }
            sessionIdRef.current = sessionId - 1;
            silenceTimer.stop();
            onStopRef.current?.();
            connectionRef.current = null;
            setMediaStream(null);
            // eslint-disable-next-line no-void
            void reportDuration();
            setTranscriptionState('stopped');
         };

         /*
          * Helper: handle a fatal error from the SDK.
          */
         const handleFatalError = (code: TranscriptionErrorCode): void => {
            if (sessionIdRef.current !== sessionId) {
               return;
            }
            sessionIdRef.current = sessionId - 1;
            silenceTimer.stop();
            setErrorCode(code);
            setTranscriptionState('error');
            onErrorRef.current?.(code);
            connectionRef.current?.close();
            connectionRef.current = null;
            setMediaStream(null);
            // eslint-disable-next-line no-void
            void reportDuration();
         };

         const deviceId = await resolveAudioInputDeviceConstraint(deviceRef.current?.id ?? null);

         // eslint-disable-next-line init-declarations
         let connection: RealtimeConnection;
         try {
            connection = Scribe.connect({
               token: token,
               baseUri: 'https://api.eu.residency.elevenlabs.io',
               modelId: 'scribe_v2_realtime',
               languageCode: 'pl',
               commitStrategy: CommitStrategy.VAD,
               vadSilenceThresholdSecs: config.vadSilenceThresholdSecs,
               vadThreshold: config.vadThreshold,
               minSpeechDurationMs: config.minSpeechDurationMs,
               minSilenceDurationMs: config.minSilenceDurationMs,
               filterBackgroundAudio: config.filterBackgroundAudio,
               microphone: {
                  deviceId: deviceId,
                  echoCancellation: config.echoCancellation,
                  noiseSuppression: config.noiseSuppression,
                  autoGainControl: config.autoGainControl,
               },
            });
         } catch (err: unknown) {
            if (sessionIdRef.current === sessionId) {
               const isDomException =
                  err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'NotFoundError');
               const code: TranscriptionErrorCode = isDomException
                  ? 'MICROPHONE_ACCESS_DENIED'
                  : 'TRANSCRIPTION_UNAVAILABLE';
               setErrorCode(code);
               setTranscriptionState('error');
               onErrorRef.current?.(code);
            }
            return;
         }

         if (sessionIdRef.current !== sessionId) {
            connection.close();
            return;
         }

         connectionRef.current = connection;

         // --- Event listeners ---

         connection.on(RealtimeEvents.OPEN, () => {
            if (sessionIdRef.current !== sessionId) {
               return;
            }
            setTranscriptionState('recording');
            startTimeRef.current = Date.now();
            silenceTimer.start();

            if (config.lowLatency) {
               /*
                * Warmup: Scribe starts processing only after receiving 2s of audio.
                * Send 2s of silence (PCM 16kHz zeros) to prime the pipeline so real
                * speech is recognized immediately when the user starts talking.
                * The microphone stream from the SDK will layer on top — send() works
                * independently of microphone mode.
                */
               sendWarmupSilence(connection);
            }
            /*
             * The SDK sets _mediaStreamTrack asynchronously after getUserMedia resolves
             * inside streamFromMicrophone. The OPEN event fires before that completes,
             * so we poll briefly until the track appears.
             */
            let pollAttempts = 0;
            const maxPollAttempts = 20; // 20 × 50ms = 1s max
            const pollInterval = setInterval(() => {
               // eslint-disable-next-line no-plusplus
               pollAttempts++;
               // eslint-disable-next-line @typescript-eslint/no-explicit-any,no-underscore-dangle
               const track = (connection as any)._mediaStreamTrack as MediaStreamTrack | undefined;
               if (track) {
                  clearInterval(pollInterval);
                  if (sessionIdRef.current === sessionId) {
                     setMediaStream(new MediaStream([track]));
                  }
               } else if (pollAttempts >= maxPollAttempts) {
                  clearInterval(pollInterval);
               }
               // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            }, 50);
         });

         connection.on(RealtimeEvents.CLOSE, () => {
            if (sessionIdRef.current !== sessionId) {
               return;
            }
            sessionIdRef.current = sessionId - 1;
            silenceTimer.stop();
            connectionRef.current = null;
            setMediaStream(null);

            if (silenceDisconnectedRef.current) {
               onSilenceDisconnectRef.current?.();
            } else {
               onStopRef.current?.();
            }
            // eslint-disable-next-line no-void
            void reportDuration();
            setTranscriptionState('stopped');
         });

         // --- Transcript events ---

         connection.on(RealtimeEvents.PARTIAL_TRANSCRIPT, (data) => {
            if (sessionIdRef.current !== sessionId) {
               return;
            }
            silenceTimer.notifyActivity();
            if (showSpeakerLabel) {
               onSpeakerTranscriptRef.current?.('spk_0', data.text, true);
            } else {
               onPartialRef.current(data.text);
            }
         });

         connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT, (data) => {
            if (sessionIdRef.current !== sessionId) {
               return;
            }
            silenceTimer.notifyActivity();
            if (showSpeakerLabel) {
               onSpeakerTranscriptRef.current?.('spk_0', data.text, false);
            } else {
               onFinalRef.current(data.text);
            }
         });

         // --- Error events ---

         connection.on(RealtimeEvents.AUTH_ERROR, () => {
            handleFatalError('TRANSCRIPTION_CREDENTIALS_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.QUOTA_EXCEEDED, () => {
            handleFatalError('TRANSCRIPTION_CREDENTIALS_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.SESSION_TIME_LIMIT_EXCEEDED, () => {
            handleGracefulStop();
         });

         connection.on(RealtimeEvents.INSUFFICIENT_AUDIO_ACTIVITY, () => {
            handleGracefulStop();
         });

         connection.on(RealtimeEvents.TRANSCRIBER_ERROR, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.INPUT_ERROR, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.RATE_LIMITED, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.RESOURCE_EXHAUSTED, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.ERROR, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.INVALID_REQUEST, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.QUEUE_OVERFLOW, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.UNACCEPTED_TERMS, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.COMMIT_THROTTLED, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });

         connection.on(RealtimeEvents.CHUNK_SIZE_EXCEEDED, () => {
            handleFatalError('TRANSCRIPTION_UNAVAILABLE');
         });
      },
      [fetchToken, reportDuration, setTranscriptionState, silenceTimer]
   );

   const stop = useCallback(() => {
      const sessionId = sessionIdRef.current;
      // Invalidate before any async work so callbacks see a stale session
      sessionIdRef.current = sessionId - 1;
      silenceTimer.stop();
      onStopRef.current?.();
      connectionRef.current?.close();
      connectionRef.current = null;
      setMediaStream(null);
      // eslint-disable-next-line no-void
      void reportDuration();
      setTranscriptionState('stopped');
   }, [reportDuration, setTranscriptionState, silenceTimer]);

   return {
      state: state,
      mediaStream: mediaStream,
      start: start,
      stop: stop,
      errorCode: errorCode,
   };
};
